import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Input,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import PageTitle from '../../components/PageTitle';
import { adminFangdongScopeCompare } from '../../api';

const { Text } = Typography;

const QUICK_USERS = [38848, 2881, 32622, 382];

const FangdongScopeCompare = () => {
  const [userIds, setUserIds] = useState('38848');
  const [detail, setDetail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);

  const runQuery = async (overrideIds) => {
    const raw = String(overrideIds != null ? overrideIds : userIds || '').trim();
    if (!raw) {
      message.warning('请填 user_id（可多个，逗号分隔，最多 5 个）');
      return;
    }
    setLoading(true);
    setPayload(null);
    try {
      // http 拦截器已 return res.data，此处 res = { success, msg, data }
      const res = await adminFangdongScopeCompare({
        user_ids: raw,
        detail: detail ? 1 : 0,
      });
      if (!res?.success) {
        message.error(res?.msg || '对比失败');
        return;
      }
      setPayload(res.data || null);
      const allMatch = !!res.data?.all_match;
      if (allMatch) {
        message.success(res.msg || '全部一致');
      } else {
        message.warning(res.msg || '存在不一致');
      }
    } catch (e) {
      message.error(e?.response?.data?.msg || e?.message || '请求失败');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'user_id',
      dataIndex: 'user_id',
      width: 100,
    },
    {
      title: '一致',
      dataIndex: 'match',
      width: 80,
      render: (v) => (v ? <Tag color="success">是</Tag> : <Tag color="error">否</Tag>),
    },
    {
      title: '旧 count / ms',
      key: 'old',
      render: (_, row) => {
        const o = row.old || {};
        if (!o.ok) return <Text type="danger">{o.error || '失败'}</Text>;
        return `${o.count} / ${o.elapsed_ms}ms`;
      },
    },
    {
      title: '新 count / ms',
      key: 'new',
      render: (_, row) => {
        const n = row.new || {};
        if (!n.ok) return <Text type="danger">{n.error || '失败'}</Text>;
        return `${n.count} / ${n.elapsed_ms}ms`;
      },
    },
    {
      title: '旧 hash',
      key: 'old_hash',
      ellipsis: true,
      render: (_, row) => row.old?.hash || '-',
    },
    {
      title: '新 hash',
      key: 'new_hash',
      ellipsis: true,
      render: (_, row) => row.new?.hash || '-',
    },
    {
      title: '仅旧 / 仅新',
      key: 'diff',
      render: (_, row) =>
        `${row.only_old_count ?? (row.only_old || []).length} / ${
          row.only_new_count ?? (row.only_new || []).length
        }`,
    },
  ];

  const results = Array.isArray(payload?.results) ? payload.results : [];

  return (
    <div>
      <PageTitle title="房源权限范围对比" />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="只读调试：旧=WHERE+Pluck；新=JSON一次+IN。正式 ListVisible 已切新路径（失败/超大IN/ENABLE=1 回退旧）。"
      />
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            style={{ width: 320 }}
            placeholder="user_id，多个用逗号，最多 5 个"
            value={userIds}
            onChange={(e) => setUserIds(e.target.value)}
            onPressEnter={() => runQuery()}
          />
          <Checkbox checked={detail} onChange={(e) => setDetail(e.target.checked)}>
            返回完整 id 列表
          </Checkbox>
          <Button type="primary" loading={loading} onClick={() => runQuery()}>
            对比
          </Button>
        </Space>
        <div style={{ marginTop: 12 }}>
          <Text type="secondary">大户快捷：</Text>
          <Space wrap style={{ marginLeft: 8 }}>
            {QUICK_USERS.map((id) => (
              <Button
                key={id}
                size="small"
                disabled={loading}
                onClick={() => {
                  setUserIds(String(id));
                  runQuery(String(id));
                }}
              >
                {id}
              </Button>
            ))}
          </Space>
        </div>
      </Card>

      {payload && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap>
            <Text>生成时间：{payload.generated_at || '-'}</Text>
            <Tag color={payload.all_match ? 'success' : 'error'}>
              全部一致：{payload.all_match ? '是' : '否'}
            </Tag>
          </Space>
          {payload.note ? (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">{payload.note}</Text>
            </div>
          ) : null}
        </Card>
      )}

      <Table
        rowKey={(r) => String(r.user_id)}
        loading={loading}
        columns={columns}
        dataSource={results}
        pagination={false}
        size="small"
        expandable={{
          expandedRowRender: (row) => (
            <div>
              <div style={{ marginBottom: 8 }}>
                <Text strong>仅旧有：</Text>
                <Text code copyable>
                  {(row.only_old || []).join(',') || '(空)'}
                </Text>
              </div>
              <div style={{ marginBottom: 8 }}>
                <Text strong>仅新有：</Text>
                <Text code copyable>
                  {(row.only_new || []).join(',') || '(空)'}
                </Text>
              </div>
              {detail && row.old?.ids ? (
                <div style={{ marginBottom: 8 }}>
                  <Text strong>旧 ids：</Text>
                  <Text code copyable style={{ wordBreak: 'break-all' }}>
                    {(row.old.ids || []).join(',')}
                  </Text>
                </div>
              ) : null}
              {detail && row.new?.ids ? (
                <div>
                  <Text strong>新 ids：</Text>
                  <Text code copyable style={{ wordBreak: 'break-all' }}>
                    {(row.new.ids || []).join(',')}
                  </Text>
                </div>
              ) : null}
            </div>
          ),
        }}
      />
    </div>
  );
};

export default FangdongScopeCompare;
